import api from 'api'
import type {
  JsonApiList,
  SurveyTemplate,
  SurveyTemplatePayload,
  SurveyTemplateRow,
  SurveyTemplatesMeta,
  SurveyTemplatesParams,
} from 'pages/administration/SurveyBank/common/types'

export type SurveyTemplatesResponse = JsonApiList<
  SurveyTemplateRow,
  SurveyTemplatesMeta
>

interface JsonApiItem<Attributes> {
  data: { id: string; type: string; attributes: Attributes }
}

const basePath = 'administration/survey_templates'

const unwrap = <Attributes>({ data }: { data: JsonApiItem<Attributes> }) =>
  data.data.attributes

// The shared axios instance snake_cases request bodies but not query params,
// so the API parameter names are spelled out here.
const list = ({ search, status, page, perPage }: SurveyTemplatesParams) =>
  api.get<SurveyTemplatesResponse>(basePath, {
    params: {
      q: search || undefined,
      status,
      page,
      per_page: perPage,
    },
  })

const show = (id: number) =>
  api.get<JsonApiItem<SurveyTemplate>>(`${basePath}/${id}`).then(unwrap)

const create = (payload: SurveyTemplatePayload) =>
  api.post<JsonApiItem<SurveyTemplate>>(basePath, payload).then(unwrap)

const update = (id: number, payload: SurveyTemplatePayload) =>
  api
    .patch<JsonApiItem<SurveyTemplate>>(`${basePath}/${id}`, payload)
    .then(unwrap)

const duplicate = (id: number) =>
  api
    .post<JsonApiItem<SurveyTemplate>>(`${basePath}/${id}/duplicate`)
    .then(unwrap)

const archive = (id: number) =>
  api
    .patch<JsonApiItem<SurveyTemplate>>(`${basePath}/${id}/archive`)
    .then(unwrap)

const restore = (id: number) =>
  api
    .patch<JsonApiItem<SurveyTemplate>>(`${basePath}/${id}/restore`)
    .then(unwrap)

export const surveyTemplatesService = {
  list,
  show,
  create,
  update,
  duplicate,
  archive,
  restore,
}
